import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import classNames from 'classnames';
import moment from 'moment';
import styles from './css/index.module.scss';
import PageTitle from '../../components/PageTitle';
import sendRequest from '../../helpers/sendRequest';
import Api from '../../Api';
import { resolvePhotoUrl } from '../../helpers/resolvePhotoUrl';
import IconButton from '../../components/IconButton';
import CancelEditingIcon from '../../assets/formIcons/cancelEditing.svg';
import ChangePhotosIcon from '../../assets/formIcons/photosSmall.svg';
import ChangeOrgIcon from '../../assets/formIcons/changeOrg.svg';
import MapView from '../ViewSingleOrganization/components/MapView';
import { EVENT_SOURCE_LABELS } from '../Events/constants';
import { formatEventHoldingDates } from '../../helpers/formatHoldingDatesList';

function Field({ label, value, grey = false, multiline = false, wide = false }) {
  const displayValue = value != null && String(value).trim() !== '' ? value : '—';

  return (
    <div className={classNames(styles.field, { [styles.fieldWide]: wide })}>
      <span className={styles.fieldLabel}>{label}</span>
      <span className={classNames(styles.fieldValue, { [styles.textGray]: grey, [styles.multiline]: multiline })}>
        {displayValue}
      </span>
    </div>
  );
}

function formatSource(source) {
  if (!source) return '—';
  return EVENT_SOURCE_LABELS[source] || source;
}

function formatStatus(isActive) {
  if (isActive === true) return 'Активно';
  if (isActive === false) return 'Забанено';
  return '—';
}

function ViewSingleEvent() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('id');

  const [gettingLoading, setGettingLoading] = useState(true);
  const [gettingError, setGettingError] = useState('');
  const [singleEvent, setSingleEvent] = useState(null);

  const photos = useMemo(
    () => (singleEvent?.carousel_photos?.length ? singleEvent.carousel_photos : []),
    [singleEvent],
  );

  const onCancelClick = () => {
    navigate('/events/view');
  };

  const onEditPhotoClick = () => {
    navigate(`/events/edit-photos?id=${singleEvent._id}`);
  };

  const onEditClick = () => {
    navigate(`/events/edit?id=${singleEvent._id}`);
  };

  const initData = async () => {
    const data = await sendRequest({
      request: Api.getEvent,
      payload: { id: eventId },
      warnErrorText: 'while getting single event',
      setLoading: setGettingLoading,
    });

    if (data?.status !== 'ok') {
      setGettingError(typeof data.message === 'string' ? data.message : 'Ошибка');
    }

    setSingleEvent(data.event);
  };

  useEffect(() => {
    initData().then();
  }, []);

  return (
    <div className={styles.wrapper}>
      <PageTitle
        text="Просмотр мероприятия"
        RightComponent={(
          <div className={styles.buttonsRow}>
            <IconButton
              svgPath={CancelEditingIcon}
              tooltipText="Отменить"
              onClick={onCancelClick}
            />
            <IconButton
              svgPath={ChangePhotosIcon}
              tooltipText="Изменить фото"
              onClick={onEditPhotoClick}
            />
            <IconButton
              svgPath={ChangeOrgIcon}
              tooltipText="Редактировать"
              onClick={onEditClick}
            />
          </div>
        )}
      />
      {gettingLoading
        ? <p className={styles.infoText}>Загрузка</p>
        : gettingError
          ? <p className={styles.infoText}>{gettingError}</p>
          : singleEvent ? (
            <div className={styles.container}>
              <div className={styles.column}>
                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Основное</p>
                  <div className={styles.grid}>
                    <Field label="Название" value={singleEvent.name} wide />
                    <Field label="Источник" value={formatSource(singleEvent.source)} />
                    <Field label="Статус" value={formatStatus(singleEvent.is_active)} />
                    <Field label="Дата проведения" value={formatEventHoldingDates(singleEvent)} />
                    <Field label="Дата создания" value={moment(singleEvent.createdAt).format('DD.MM.YYYY HH:mm')} />
                    <Field label="ID мероприятия" value={singleEvent._id} grey />
                    <Field label="Описание" value={singleEvent.description} multiline wide />
                  </div>
                </div>

                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Тип мероприятия</p>
                  <div className={styles.grid}>
                    <Field label="Категория афиши" value={singleEvent.events_category?.name || 'Без категории'} />
                  </div>
                </div>

                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Владелец</p>
                  <div className={styles.grid}>
                    <Field label="Email" value={singleEvent.user?.email} />
                    <Field
                      label="Дата регистрации"
                      value={singleEvent.user?.createdAt ? moment(singleEvent.user.createdAt).format('DD.MM.YYYY') : '—'}
                    />
                    <Field label="ID владельца" value={singleEvent.user?._id} grey />
                  </div>
                </div>
              </div>

              <div className={styles.column}>
                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Местоположение</p>
                  <div className={styles.grid}>
                    <Field label="Страна" value={singleEvent.country?.name} />
                    <Field
                      label="Город"
                      value={singleEvent.is_in_all_cities_of_country
                        ? 'Все города'
                        : singleEvent.city?.name}
                    />
                    <Field
                      label="Адрес"
                      value={singleEvent.is_in_all_cities_of_country
                        ? singleEvent.country?.name
                        : singleEvent.address}
                      wide
                    />
                  </div>

                  {singleEvent.city?._id && singleEvent.coordinates && (
                    <div className={styles.mapWrap}>
                      <MapView
                        city={singleEvent.city}
                        coordinates={singleEvent.coordinates}
                        setCoordinates={() => {}}
                        defaultZoom={12}
                      />
                    </div>
                  )}
                </div>

                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Контакты</p>
                  <div className={styles.grid}>
                    <Field label="Телефон" value={singleEvent.contacts?.phone_number} />
                    <Field label="Instagram" value={singleEvent.contacts?.instagram} />
                    <Field label="Telegram" value={singleEvent.contacts?.telegram} />
                    <Field label="WhatsApp" value={singleEvent.contacts?.whatsapp} />
                    <Field label="Viber" value={singleEvent.contacts?.viber} />
                    <Field label="Facebook" value={singleEvent.contacts?.facebook} />
                    <Field label="Сайт" value={singleEvent.contacts?.website} wide />
                  </div>
                </div>
              </div>

              <div className={styles.column}>
                <div className={styles.infoBlock}>
                  <p className={styles.blockTitle}>Фото</p>
                  {photos.length ? (
                    <div className={styles.list}>
                      {photos.map((photo, index) => {
                        const src = resolvePhotoUrl(photo, Api.url);
                        if (!src) return null;
                        return (
                          <img
                            className={styles.photo}
                            key={photo._id || index}
                            src={src}
                            alt="Фото мероприятия"
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <p className={styles.emptyText}>Нет фото</p>
                  )}
                </div>
              </div>
            </div>
          ) : null}
    </div>
  );
}

export default ViewSingleEvent;
